import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  FACT_SHEET,
  COMMENTARY,
  ATTRIBUTION,
  PRODUCT_UPDATES,
  SERVICE_LABEL_WHOLESALER,
} from '../Helper/constants.js'
import {
  getActiveProductResults,
  getFundsByProductType,
  getSubscribedLiteratureForFunds,
  findFundById,
  findLiteratureById,
  getWholesalerLabel,
} from '../Helper/helper.js'

import ProductInsights from './ProductInsights.jsx'

import AddProductModal from './AddProductModal.jsx'
import './products.css'

function Products(props) {
  const [userSubscriptionProfile] = useState(props.userSubscriptionProfile)
  const [showProductModal, setShowProductModal] = useState(false)

  const {
    productTypes,
    wholesalerLabels,
    addToServiceQueue,
    updateFunds,
    handleUnsubscribeFund,
    funds,
    subscriptions,
    handleSubscribe,
  } = props

  const activeProductResults = getActiveProductResults(subscriptions)

  useEffect(() => {
    if (showProductModal) {
      // preselect the first lit doc
      funds.forEach((fund) => {
        const activeLiterature = fund.activeLiterature
        const hasAnythingPreselected = activeLiterature.find(
          (doc) => doc.preSelected
        )
        if (!hasAnythingPreselected && activeLiterature.length > 0) {
          activeLiterature[0].preSelected = true
        }
      })
    }
  }, [showProductModal])

  const getLiteratureByName = (literatureDocs, name) => {
    const foundDocs = []

    if (!literatureDocs) {
      return null
    }

    literatureDocs.map((doc) => {
      if (
        (name === 'Commentary' && doc.name.indexOf(name) >= 0) ||
        doc.name === name
      ) {
        foundDocs.push(doc)
      }
    })

    return foundDocs
  }

  const handleAddProductClick = () => {
    setShowProductModal(true)
  }

  const handleCloseModalClick = () => {
    setShowProductModal(false)
  }

  const handleSubscriptionCheckBoxClicked = (event, docs) => {
    const fundId = event.target.dataset.fundId
    const serviceQueue = []

    const _funds = JSON.parse(JSON.stringify(funds))

    const fund = findFundById(_funds, fundId)

    docs.map((doc) => {
      const document = findLiteratureById(fund.activeLiterature, doc.id)

      document.selected = event.target.checked
      document.preSelected = document.selected

      serviceQueue.push({
        id: doc.id,
        selected: document.selected,
      })
    })

    updateFunds(_funds)

    addToServiceQueue(serviceQueue)
  }

  const renderCheckBox = (fund, docs) => {
    const doc = docs[0]
    return docs?.length ? (
      <React.Fragment>
        <input
          type="checkbox"
          id={'product-cb-' + doc.id}
          data-fund-id={fund.id}
          onChange={(e) => handleSubscriptionCheckBoxClicked(e, docs)}
          value={doc.id}
          checked={doc.selected}
          className="Subscription-checkbox"
          data-analytics="sc-subscribe-checkbox"
        ></input>
        <label
          htmlFor={'product-cb-' + doc.id}
          className="u-sm-marginBottom3gu u-md-marginBottom3gu"
        >
          <p className="u-lg-hidden u-marginLeft2gu u-inlineBlock">
            {doc.name}
          </p>
        </label>
      </React.Fragment>
    ) : (
      <span className="Not-applicable u-sm-hidden u-md-hidden u-marginTop1gu">
        <p className="u-marginLeft3gu" aria-label="Not applicable">
          &mdash;
        </p>
      </span>
    )
  }

  const handleSelectAllLiteratureType = (event) => {
    const literatureType = event.target.dataset.literatureType

    const _funds = JSON.parse(JSON.stringify(funds))

    const docsToCallService = []

    _funds.map((fund) => {
      // get all fund docs that have any subscribed literature
      const literatureDocs = getSubscribedLiteratureForFunds(fund)

      if (literatureDocs.length > 0) {
        // get the doc from the list of active literature for that fund and select it
        const docs = getLiteratureByName(fund.activeLiterature, literatureType)

        docs.map((doc) => {
          if (doc && !doc.selected) {
            docsToCallService.push({
              id: doc.id,
              selected: true,
            })

            doc.selected = true
            doc.preSelected = doc.selected
          }
        })
      }
    })

    addToServiceQueue(docsToCallService)

    updateFunds(_funds)
  }

  return (
    <React.Fragment>
      <hr className="Rule u-textColorBrand" />
      <div
        id="Products"
        data-analytics="cesc-Products"
        className="Card Flex u-lg-sizeConstrained u-marginBottom4gu u-bgColorWhite u-sm-paddingHoriz3gu u-sm-paddingVert6gu u-sm-marginBottom6gu"
      >
        <h2 className="u-marginBottom6gu u-inlineBlock">Product</h2>
        <button
          onClick={handleAddProductClick}
          className="js-add-product-btn Button Button--tertiary u-marginLeft3gu u-textKernDefault u-textNoTransform"
          data-tracking="secondary"
        >
          <span className="Icon--material u-textSizeMsr u-textColorLight u-textBold u-marginRight1gu">
            add_circle_outline
          </span>
          Add Product
        </button>

        {/* DESKTOP */}
        <div className="Grid u-sm-hidden u-md-hidden">
          <div className="u-sizeFull">
            <div className="Table-overflowWrapper">
              <table className="Table">
                <colgroup>
                  <col className="u-size3of8" />
                  <col className="u-size1of8" />
                  <col className="u-size1of8" />
                  <col className="u-size1of8" />
                  <col className="u-size1of8" />
                  <col className="u-size1of8" />
                </colgroup>
                <thead className="Table-head Add-products-table">
                  <tr className="Table-row">
                    <th className="Table-header u-textNormal u-borderColorNeutralLight u-borderBottom0 u-borderTop0 u-borderLeft0 u-borderRight">
                      Product
                    </th>
                    <th className="Table-header u-textNormal u-textCenter u-paddingBottom0 u-paddingHoriz0">
                      <div
                        className="Tooltip-target Tooltip-target--reversed u-posRelative u-textOpen"
                        aria-describedby="Tooltip-fact-sheet"
                        role="button"
                        tabIndex="0"
                      >
                        Fact Sheet
                        <div
                          className="Tooltip u-posAbsolute"
                          role="tooltip"
                          id="Tooltip-fact-sheet"
                        >
                          <div className="Tooltip-content Content">
                            <p className="u-textSizeMsrm1">
                              Product overview with strategy, performance, top
                              holdings and portfolio manager information
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        data-literature-type={FACT_SHEET}
                        onClick={handleSelectAllLiteratureType}
                        className="Button Button--tertiary Button--reversed Button--small u-marginBottom1gu u-textNoTransform u-textBold u-border0 u-padding0 u-bgColorTransparent u-marginVert2gu u-textKernDefault"
                        data-analytics="cesc-select-all"
                      >
                        Select all
                      </button>
                    </th>
                    <th className="Table-header u-textNormal u-textCenter u-paddingBottom0 u-paddingHoriz0">
                      <div
                        className="Tooltip-target Tooltip-target--reversed u-posRelative u-textOpen"
                        aria-describedby="Tooltip-commentary"
                        role="button"
                        tabIndex="0"
                      >
                        Commentary
                        <div
                          className="Tooltip u-posAbsolute"
                          role="tooltip"
                          id="Tooltip-commentary"
                        >
                          <div className="Tooltip-content Content">
                            <p className="u-textSizeMsrm1">
                              Update on how market forces are influencing a
                              product’s performance, holdings, positioning, etc.
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        data-literature-type={COMMENTARY}
                        onClick={handleSelectAllLiteratureType}
                        className="Button Button--tertiary Button--reversed Button--small u-marginBottom1gu u-textNoTransform u-textBold u-border0 u-padding0 u-bgColorTransparent u-marginVert2gu u-textKernDefault"
                        data-analytics="cesc-select-all"
                      >
                        Select all
                      </button>
                    </th>
                    <th className="Table-header u-textNormal u-textCenter u-paddingBottom0 u-paddingHoriz0">
                      <div
                        className="Tooltip-target Tooltip-target--reversed u-posRelative u-textOpen"
                        aria-describedby="Tooltip-attribution-contribution"
                        role="button"
                        tabIndex="0"
                      >
                        Attribution/
                        <br />
                        Contribution
                        <div
                          className="Tooltip u-posAbsolute"
                          role="tooltip"
                          id="Tooltip-attribution-contribution"
                        >
                          <div className="Tooltip-content Content">
                            <p className="u-textSizeMsrm1">
                              Explains contributors and detractors to a
                              product’s performance
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        data-literature-type={ATTRIBUTION}
                        onClick={handleSelectAllLiteratureType}
                        className="Button Button--tertiary Button--reversed Button--small u-marginBottom1gu u-textNoTransform u-textBold u-border0 u-padding0 u-bgColorTransparent u-marginVert2gu u-textKernDefault"
                        data-analytics="cesc-select-all"
                      >
                        Select all
                      </button>
                    </th>
                    <th className="Table-header u-textNormal u-textCenter u-paddingBottom0 u-paddingHoriz0">
                      <div
                        className="Tooltip-target Tooltip-target--reversed u-posRelative u-textOpen"
                        aria-describedby="Tooltip-product-updates"
                        role="button"
                        tabIndex="0"
                      >
                        Product Updates
                        <div
                          className="Tooltip u-posAbsolute"
                          role="tooltip"
                          id="Tooltip-product-updates"
                        >
                          <div className="Tooltip-content Content">
                            <p className="u-textSizeMsrm1">
                              Periodic alerts related to a product
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        data-literature-type={PRODUCT_UPDATES}
                        onClick={handleSelectAllLiteratureType}
                        className="Button Button--tertiary Button--reversed Button--small u-marginBottom1gu u-textNoTransform u-textBold u-border0 u-padding0 u-bgColorTransparent u-marginVert2gu u-textKernDefault"
                        data-analytics="cesc-select-all"
                      >
                        Select all
                      </button>
                    </th>
                    <th
                      className="Table-header Remove-column u-paddingBottom0 u-paddingHoriz0 u-textKernDefault"
                      aria-hidden="true"
                    >
                      Remove
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="Accordion Accordion--ruled">
                {productTypes.map((productType) => {
                  const fundsByProductType = getFundsByProductType(
                    funds,
                    productType.key
                  )

                  let hasDocs = false

                  fundsByProductType.forEach((fund) => {
                    const literatureDocs = getSubscribedLiteratureForFunds(fund)

                    if (literatureDocs.length > 0) {
                      hasDocs = hasDocs || true
                    }
                  })
                  let viewCount = 0

                  return (
                    productType.key !== 'all' &&
                    hasDocs && (
                      <details
                        key={'details' + productType.key}
                        className="Accordion-item"
                        open
                      >
                        <summary className="Accordion-toggle u-textColorBrand u-bgColorLight u-padding3gu">
                          <h4 className="u-marginLeft4gu u-textColorBrand">
                            {productType.label}
                          </h4>
                        </summary>
                        <div className="Accordion-content Content u-marginLeft3gu u-paddingTop0">
                          <div className="Table-overflowWrapper">
                            <table className="Table Table--striped">
                              <colgroup>
                                <col className="u-size3of8" />
                                <col className="u-size1of8" />
                                <col className="u-size1of8" />
                                <col className="u-size1of8" />
                                <col className="u-size1of8" />
                                <col className="u-size1of8" />
                              </colgroup>
                              <tbody className="Table-body">
                                {fundsByProductType
                                  .sort((a, b) =>
                                    (a.name || '').localeCompare(
                                      b.name || '',
                                      'en',
                                      { sensitivity: 'base' }
                                    )
                                  )
                                  .map((fund) => {
                                    const subscribedLiteratureDocs =
                                      getSubscribedLiteratureForFunds(fund)
                                    const literatureDocs = fund.activeLiterature

                                    const factSheet = getLiteratureByName(
                                      literatureDocs,
                                      FACT_SHEET
                                    )
                                    const factSheetWholesalerLabel =
                                      Boolean(factSheet?.length) &&
                                      getWholesalerLabel(
                                        wholesalerLabels,
                                        factSheet.wholesalerLabel
                                      )

                                    const commentary = getLiteratureByName(
                                      literatureDocs,
                                      COMMENTARY
                                    )
                                    const commentaryWholesalerLabel =
                                      Boolean(commentary?.length) &&
                                      getWholesalerLabel(
                                        wholesalerLabels,
                                        commentary.wholesalerLabel
                                      )

                                    const atttribution = getLiteratureByName(
                                      literatureDocs,
                                      ATTRIBUTION
                                    )
                                    const atttributionWholesalerLabel =
                                      Boolean(atttribution?.length) &&
                                      getWholesalerLabel(
                                        wholesalerLabels,
                                        atttribution.wholesalerLabel
                                      )

                                    const productUpdate = getLiteratureByName(
                                      literatureDocs,
                                      PRODUCT_UPDATES
                                    )
                                    const productUpdateWholesalerLabel =
                                      Boolean(productUpdate?.length) &&
                                      getWholesalerLabel(
                                        wholesalerLabels,
                                        productUpdate.wholesalerLabel
                                      )

                                    return (
                                      subscribedLiteratureDocs.length > 0 &&
                                      literatureDocs.length > 0 && (
                                        <tr
                                          id={'fundId' + fund.id}
                                          data-index={viewCount++}
                                          key={'funds' + fund.id}
                                          className="Table-row"
                                        >
                                          <td className="Table-cell u-borderColorNeutralLight u-borderBottom0 u-borderTop0 u-borderLeft0 u-borderRight u-paddingTop4gu u-paddingBottom1gu u-paddingLeft0">
                                            <h4>{fund.name}</h4>
                                          </td>
                                          <td className="Table-cell u-textCenter u-paddingTop4gu">
                                            {renderCheckBox(fund, factSheet)}
                                            {userSubscriptionProfile.role ===
                                              SERVICE_LABEL_WHOLESALER &&
                                              factSheet &&
                                              factSheet.wholesalerLabel &&
                                              factSheetWholesalerLabel && (
                                                <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                                  <div className="u-textSizeMsrm1 u-textNoWrap">
                                                    {factSheetWholesalerLabel}
                                                  </div>
                                                </div>
                                              )}
                                          </td>
                                          <td className="Table-cell u-textCenter u-paddingTop4gu">
                                            {renderCheckBox(fund, commentary)}
                                            {userSubscriptionProfile.role ===
                                              SERVICE_LABEL_WHOLESALER &&
                                              commentary &&
                                              commentary.wholesalerLabel &&
                                              commentaryWholesalerLabel && (
                                                <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                                  <div className="u-textSizeMsrm1 u-textNoWrap">
                                                    {commentaryWholesalerLabel}
                                                  </div>
                                                </div>
                                              )}
                                          </td>
                                          <td className="Table-cell u-textCenter u-paddingTop4gu">
                                            {renderCheckBox(fund, atttribution)}
                                            {userSubscriptionProfile.role ===
                                              SERVICE_LABEL_WHOLESALER &&
                                              atttribution &&
                                              atttribution.wholesalerLabel &&
                                              atttributionWholesalerLabel && (
                                                <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                                  <div className="u-textSizeMsrm1 u-textNoWrap">
                                                    {
                                                      atttributionWholesalerLabel
                                                    }
                                                  </div>
                                                </div>
                                              )}
                                          </td>
                                          <td className="Table-cell u-textCenter u-paddingTop4gu">
                                            {renderCheckBox(
                                              fund,
                                              productUpdate
                                            )}
                                            {userSubscriptionProfile.role ===
                                              SERVICE_LABEL_WHOLESALER &&
                                              productUpdate &&
                                              productUpdate.wholesalerLabel &&
                                              productUpdateWholesalerLabel && (
                                                <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                                  <div className="u-textSizeMsrm1 u-textNoWrap">
                                                    {
                                                      productUpdateWholesalerLabel
                                                    }
                                                  </div>
                                                </div>
                                              )}
                                          </td>
                                          <td className="Table-cell u-textCenter">
                                            <button
                                              onClick={handleUnsubscribeFund}
                                              data-fund-id={fund.id}
                                              className="Button Button--tertiary u-textNoTransform u-textKernDefault"
                                              data-analytics="cesc-remove"
                                            >
                                              Remove
                                            </button>
                                          </td>
                                        </tr>
                                      )
                                    )
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </details>
                    )
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* MOBILE + TABLET */}
        <div id="Esc-products-mobile" className="Grid u-lg-hidden">
          <div className="u-sizeFull">
            <table className="Table">
              <colgroup>
                <col className="u-sizeFull" />
              </colgroup>
              <thead className="Table-head">
                <tr className="Table-row">
                  <th className="Table-header u-borderColorNeutralLight u-borderBottom0 u-borderTop0 u-borderLeft0 u-borderRight">
                    <h4 className="u-textColorWhite u-textReversed">Product</h4>
                  </th>
                </tr>
              </thead>
            </table>
            <div className="Accordion Accordion--ruled">
              {productTypes.map((productType) => {
                const fundsByProductType = getFundsByProductType(
                  funds,
                  productType.key
                )

                let hasDocs = false

                fundsByProductType.forEach((fund) => {
                  const literatureDocs = getSubscribedLiteratureForFunds(fund)

                  if (literatureDocs.length > 0) {
                    hasDocs = hasDocs || true
                  }
                })

                return (
                  productType.key !== 'all' &&
                  hasDocs && (
                    <details
                      key={'details' + productType.key}
                      className="Accordion-item"
                      open
                    >
                      <summary className="Accordion-toggle u-textColorBrand u-bgColorLight u-padding3gu">
                        <h4 className="u-marginLeft4gu u-textColorBrand">
                          {productType.label}
                        </h4>
                      </summary>
                      <div className="Accordion-content u-margin0 u-padding0">
                        <div className="Grid-striper">
                          {fundsByProductType
                            .sort((a, b) =>
                              (a.name || '').localeCompare(b.name || '', 'en', {
                                sensitivity: 'base',
                              })
                            )
                            .map((fund) => {
                              const subscribedLiteratureDocs =
                                getSubscribedLiteratureForFunds(fund)
                              const literatureDocs = fund.activeLiterature

                              const factSheet = getLiteratureByName(
                                literatureDocs,
                                FACT_SHEET
                              )
                              const factSheetWholesalerLabel =
                                Boolean(factSheet?.length) &&
                                getWholesalerLabel(
                                  wholesalerLabels,
                                  factSheet.wholesalerLabel
                                )

                              const commentary = getLiteratureByName(
                                literatureDocs,
                                COMMENTARY
                              )
                              const commentaryWholesalerLabel =
                                Boolean(commentary?.length) &&
                                getWholesalerLabel(
                                  wholesalerLabels,
                                  commentary.wholesalerLabel
                                )

                              const atttribution = getLiteratureByName(
                                literatureDocs,
                                ATTRIBUTION
                              )
                              const atttributionWholesalerLabel =
                                Boolean(atttribution?.length) &&
                                getWholesalerLabel(
                                  wholesalerLabels,
                                  atttribution.wholesalerLabel
                                )

                              const productUpdate = getLiteratureByName(
                                literatureDocs,
                                PRODUCT_UPDATES
                              )
                              const productUpdateWholesalerLabel =
                                Boolean(productUpdate?.length) &&
                                getWholesalerLabel(
                                  wholesalerLabels,
                                  productUpdate.wholesalerLabel
                                )

                              return (
                                subscribedLiteratureDocs.length > 0 &&
                                literatureDocs.length > 0 && (
                                  <div
                                    id={'mobileFundId' + fund.id}
                                    key={'funds' + fund.id}
                                    className="Grid u-padding3gu"
                                  >
                                    <div>
                                      <h4>{fund.name}</h4>
                                      <button
                                        onClick={handleUnsubscribeFund}
                                        data-fund-id={fund.id}
                                        className="Button Button--tertiary u-padding0 u-block u-marginBottom3gu u-textNoTransform u-textKernDefault"
                                        data-analytics="cesc-remove"
                                      >
                                        Remove product
                                      </button>
                                    </div>
                                    {factSheet && (
                                      <div>
                                        {renderCheckBox(fund, factSheet)}
                                        {userSubscriptionProfile.role ===
                                          SERVICE_LABEL_WHOLESALER &&
                                          factSheet &&
                                          factSheet.wholesalerLabel &&
                                          factSheetWholesalerLabel && (
                                            <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                              <div className="u-textSizeMsrm1 u-textNoWrap">
                                                {factSheetWholesalerLabel}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {commentary && (
                                      <div>
                                        {renderCheckBox(fund, commentary)}
                                        {userSubscriptionProfile.role ===
                                          SERVICE_LABEL_WHOLESALER &&
                                          commentary &&
                                          commentary.wholesalerLabel &&
                                          commentaryWholesalerLabel && (
                                            <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                              <div className="u-textSizeMsrm1 u-textNoWrap">
                                                {commentaryWholesalerLabel}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {atttribution && (
                                      <div>
                                        {renderCheckBox(fund, atttribution)}
                                        {userSubscriptionProfile.role ===
                                          SERVICE_LABEL_WHOLESALER &&
                                          atttribution &&
                                          atttribution.wholesalerLabel &&
                                          atttributionWholesalerLabel && (
                                            <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                              <div className="u-textSizeMsrm1 u-textNoWrap">
                                                {atttributionWholesalerLabel}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {productUpdate && (
                                      <div>
                                        {renderCheckBox(fund, productUpdate)}
                                        {userSubscriptionProfile.role ===
                                          SERVICE_LABEL_WHOLESALER &&
                                          productUpdate &&
                                          productUpdate.wholesalerLabel &&
                                          productUpdateWholesalerLabel && (
                                            <div className="From-you u-marginTop1gu u-marginBottom2gu u-inlineBlock u-bgColorLight u-paddingHoriz2gu u-textNormal">
                                              <div className="u-textSizeMsrm1 u-textNoWrap">
                                                {productUpdateWholesalerLabel}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </div>
                                )
                              )
                            })}
                        </div>
                      </div>
                    </details>
                  )
                )
              })}
            </div>
          </div>
        </div>

        {activeProductResults && activeProductResults.length > 0 && (
          <hr className="Rule Rule--thin u-marginVert6gu" role="presentation" />
        )}
        <ProductInsights
          productResults={activeProductResults}
          wholesalerLabels={wholesalerLabels}
          userSubscriptionProfile={userSubscriptionProfile}
          handleSubscribe={handleSubscribe}
        />
      </div>

      {showProductModal && (
        <AddProductModal
          handleCloseModalClick={handleCloseModalClick}
          funds={funds}
          updateFunds={updateFunds}
          productTypes={productTypes}
          assetClasses={props.assetClasses}
          wholesalerLabels={wholesalerLabels}
          userSubscriptionProfile={userSubscriptionProfile}
          addToServiceQueue={addToServiceQueue}
        />
      )}
    </React.Fragment>
  )
}

Products.propTypes = {
  funds: PropTypes.array.isRequired,
  assetClasses: PropTypes.array.isRequired,
  productTypes: PropTypes.array.isRequired,
  wholesalerLabels: PropTypes.array.isRequired,
  userSubscriptionProfile: PropTypes.object.isRequired,
  addToServiceQueue: PropTypes.func.isRequired,
  handleUnsubscribeFund: PropTypes.func.isRequired,
  updateFunds: PropTypes.func.isRequired,
  subscriptions: PropTypes.array.isRequired,
  handleSubscribe: PropTypes.func.isRequired,
}

export default Products
